import { useState } from "react";
import { Link } from "react-router-dom";
import { useNotifications } from "../../app/providers/NotificationProvider";
import { Icon } from "../../components/Icon";
import {
  markAllNotificationsRead,
  markNotificationRead,
} from "../../lib/api-notification";
import type { Notification, ReminderKind } from "../../types/notification";
import { PushPermissionCard } from "./PushPermissionCard";

const reminderCopy: Record<ReminderKind, string> = {
  event_reminder_14d: "До события осталось 14 дней",
  event_reminder_3d: "До события осталось 3 дня",
  event_reminder_today: "Событие уже сегодня",
};

function displayDate(value: string): string {
  return new Intl.DateTimeFormat("ru-RU", {
    day: "numeric",
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(`${value}T00:00:00Z`));
}

function NotificationCard({
  item,
  pending,
  onRead,
}: {
  item: Notification;
  pending: boolean;
  onRead: (id: string) => void;
}) {
  const unread = item.read_at === null;
  return (
    <li>
      <article
        className={`relative rounded-[24px] border p-5 shadow-[0_14px_36px_rgba(35,33,25,0.06)] transition-colors sm:p-6 ${
          unread ? "border-[#F4C48F] bg-[#FFF9F1]" : "border-transparent bg-white"
        }`}
      >
        <div className="flex items-start gap-4">
          <span
            className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl ${
              unread ? "bg-[#FF7A00] text-white" : "bg-[#F2F1EC] text-[#77776F]"
            }`}
          >
            <Icon name="bell" size={21} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#C55E00]">
                {reminderCopy[item.kind]}
              </p>
              {unread && (
                <span className="rounded-full bg-[#FF7A00] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">
                  Не прочитано
                </span>
              )}
            </div>
            <h2 className="mt-1 text-xl font-extrabold tracking-[-0.035em] text-[#171714]">
              {item.event.title}
            </h2>
            <time dateTime={item.event.date} className="mt-2 block text-sm font-medium text-[#77776F]">
              {displayDate(item.event.date)}
            </time>
          </div>
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-[#E3E1D9] pt-4">
          <Link
            to={`/events?event=${encodeURIComponent(item.event.id)}`}
            aria-label={`Открыть событие «${item.event.title}»`}
            className="inline-flex min-h-11 flex-1 items-center justify-center rounded-xl bg-[#171714] px-4 text-sm font-bold text-white sm:flex-none"
          >
            Открыть событие
          </Link>
          {unread && (
            <button
              type="button"
              disabled={pending}
              onClick={() => onRead(item.id)}
              aria-label={`Отметить «${item.event.title}» прочитанным`}
              className="min-h-11 flex-1 cursor-pointer rounded-xl bg-[#F2F1EC] px-4 text-sm font-bold text-[#171714] disabled:cursor-wait disabled:opacity-60 sm:flex-none"
            >
              {pending ? "Сохраняем…" : "Прочитано"}
            </button>
          )}
        </div>
      </article>
    </li>
  );
}

export function NotificationsPage() {
  const { state, refresh, applyRead, applyReadAll } = useNotifications();
  const [pendingID, setPendingID] = useState<string | "all" | null>(null);
  const [mutationError, setMutationError] = useState<string | null>(null);

  const markRead = async (id: string) => {
    setPendingID(id);
    setMutationError(null);
    try {
      const result = await markNotificationRead(id);
      applyRead(result);
    } catch {
      setMutationError("Не удалось отметить напоминание прочитанным. Попробуйте ещё раз.");
    } finally {
      setPendingID(null);
    }
  };

  const markAllRead = async () => {
    setPendingID("all");
    setMutationError(null);
    try {
      const result = await markAllNotificationsRead();
      applyReadAll(result);
    } catch {
      setMutationError("Не удалось отметить все напоминания прочитанными. Попробуйте ещё раз.");
    } finally {
      setPendingID(null);
    }
  };

  return (
    <div className="flex flex-1 flex-col px-5 pb-8 pt-7 sm:px-8 lg:px-10 lg:py-10 xl:px-14">
      <header className="mb-7 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="mb-1 text-[11px] font-bold uppercase tracking-[0.18em] text-[#FF7A00]">
            Важные даты вашей семьи
          </p>
          <h1 className="text-[32px] font-extrabold leading-none tracking-[-0.055em] text-[#171714] lg:text-[46px]">
            Напоминания
          </h1>
        </div>
        {state.status === "loaded" && state.page.unread_count > 0 && (
          <button
            type="button"
            disabled={pendingID !== null}
            onClick={() => void markAllRead()}
            className="min-h-11 cursor-pointer rounded-full bg-[#FF7A00] px-5 text-sm font-bold text-white disabled:cursor-wait disabled:bg-[#FDBA74]"
          >
            {pendingID === "all" ? "Сохраняем…" : "Прочитать все"}
          </button>
        )}
      </header>

      <PushPermissionCard />

      {mutationError && (
        <div role="alert" className="mb-4 rounded-2xl bg-[#FEF2F2] px-4 py-3 text-sm font-semibold text-[#B91C1C]">
          {mutationError}
        </div>
      )}
      {state.status === "loading" && (
        <div className="flex flex-1 items-center justify-center py-20" aria-label="Загрузка напоминаний">
          <div className="h-9 w-9 animate-spin rounded-full border-[3px] border-[#FF7A00] border-t-transparent" />
        </div>
      )}
      {state.status === "error" && (
        <div className="flex flex-1 flex-col items-center justify-center rounded-[28px] bg-white px-7 py-16 text-center">
          <h2 className="text-2xl font-extrabold">Не удалось загрузить напоминания</h2>
          <p className="mt-2 text-sm text-[#77776F]">Проверьте подключение и попробуйте снова.</p>
          <button type="button" onClick={() => void refresh(true)} className="mt-6 min-h-11 rounded-full bg-[#171714] px-7 text-sm font-semibold text-white">
            Повторить
          </button>
        </div>
      )}
      {state.status === "loaded" && state.page.items.length === 0 && (
        <div className="flex flex-1 flex-col items-center justify-center rounded-[28px] bg-white px-7 py-16 text-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-[26px] bg-[#FFF0DF] text-[#FF7A00]">
            <Icon name="bell" size={36} />
          </div>
          <h2 className="mt-6 text-2xl font-extrabold">Напоминаний пока нет</h2>
          <p className="mt-2 max-w-sm text-sm leading-6 text-[#77776F]">
            Здесь появятся напоминания о приближающихся семейных событиях.
          </p>
        </div>
      )}
      {state.status === "loaded" && state.page.items.length > 0 && (
        <ol className="grid grid-cols-1 gap-4 2xl:grid-cols-2">
          {state.page.items.map((item) => (
            <NotificationCard
              key={item.id}
              item={item}
              pending={pendingID === item.id || pendingID === "all"}
              onRead={(id) => void markRead(id)}
            />
          ))}
        </ol>
      )}
    </div>
  );
}
